import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from "react";
import ConfirmDialog, { type ConfirmDialogVariant } from "./ConfirmDialog";

// ── Types ───────────────────────────────────────────────────────────────

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmDialogVariant;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

// ── Context ─────────────────────────────────────────────────────────────

const ConfirmContext = createContext<ConfirmFn | null>(null);

/**
 * Promise-based confirmation hook.
 *
 * Usage:
 * ```tsx
 * const confirm = useConfirm();
 * const ok = await confirm({
 *   title: "Delete Watchlist",
 *   message: "This action cannot be undone.",
 *   confirmLabel: "Delete",
 *   variant: "danger",
 * });
 * if (ok) { deleteMutation.mutate(...); }
 * ```
 */
export function useConfirm(): ConfirmFn {
  const fn = useContext(ConfirmContext);
  if (!fn) throw new Error("useConfirm must be used within <ConfirmProvider>");
  return fn;
}

// ── Provider ────────────────────────────────────────────────────────────

interface DialogState extends ConfirmOptions {
  isOpen: boolean;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<DialogState>({
    isOpen: false,
    title: "",
    message: "",
  });

  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
      setState({ ...options, isOpen: true });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setState((s) => ({ ...s, isOpen: false }));
    resolveRef.current?.(true);
    resolveRef.current = null;
  }, []);

  const handleCancel = useCallback(() => {
    setState((s) => ({ ...s, isOpen: false }));
    resolveRef.current?.(false);
    resolveRef.current = null;
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialog
        isOpen={state.isOpen}
        title={state.title}
        message={state.message}
        confirmLabel={state.confirmLabel}
        cancelLabel={state.cancelLabel}
        variant={state.variant}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </ConfirmContext.Provider>
  );
}
